import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import useOpenModal, { OpenModalHookType } from '@hooks/useOpenModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import CustomerService from '@api/Customers/CustomerCtrl';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Customer, PointTransaction, PointTransactionSource } from '@api/Customers/Customer';
import { getLifetimePointsFromTransactions } from '@api/Customers/LifetimePoints';
import { showAlertMessage } from '@utils/Alerts/Message';
import OrdersService from '@api/Orders/OrderCtrl';
import { Order } from '@api/Orders/Order';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getTransactionColor, getTransactionSign, getTransactionSource, getTransactionType } from '@utils/Points';
import { ModalViewOrder } from '@components/Modals/ModalViewOrder';

interface ModalPointsHistoryProps {
    modalPointsHistory: OpenModalHookType;
    selectedCustomer?: Customer;
}

export const ModalPointsHistory: React.FC<ModalPointsHistoryProps> = (props) => {
    const { modalPointsHistory, selectedCustomer } = props;
    const customerService = useMemo(() => new CustomerService(), []);
    const ordersService = useMemo(() => new OrdersService(), []);
    const [currentPoints, setCurrentPoints] = useState<number>(selectedCustomer?.profile?.wallet?.currentPoints || 0);
    const [transactions, setTransactions] = useState<PointTransaction[]>([]);
    const [currentOrder, setCurrentOrder] = useState<Order | undefined>(undefined);
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedTransactionIndex, setSelectedTransactionIndex] = useState<number>(-1);
    const modalViewOrder = useOpenModal();

    const loadTransactions = async () => {
        showLoader();
        try {
            if (!selectedCustomer?.uid) {
                return;
            }
            // Corregir los puntos del cliente antes de mostrar su historial.
            await customerService.correctUserPointsBasedOnOrders(selectedCustomer.uid);
            let walletTransactions = selectedCustomer.profile?.wallet?.transactions || [];
            const user = await customerService.getCustomer(selectedCustomer.uid);
            setCurrentPoints(user.profile?.wallet?.currentPoints || 0);
            if (user && user.profile?.wallet?.transactions) {
                walletTransactions = user.profile?.wallet?.transactions;
            }
            const sortedTransactions = [...walletTransactions].sort((a, b) => b.date.seconds - a.date.seconds);
            setTransactions(sortedTransactions);
        } catch (error) {
            showAlertError(error);
        } finally {
            hideLoader();
        }
    };

    const handleDeleteTransaction = async (index: number) => {
        if (!selectedCustomer?.uid) return;
        try {
            showLoader();
            const transactionToDelete = transactions[index];
            await customerService.deleteTransaction(selectedCustomer.uid, transactionToDelete);
            await loadTransactions();
            showAlertMessage('Transacción eliminada correctamente');
        } catch (error) {
            showAlertError(error);
        } finally {
            hideLoader();
        }
    };

    const handleOpenOrderModal = async (orderId: string) => {
        try {
            showLoader();
            const order = await ordersService.getOrder(orderId);
            setCurrentOrder(order);
            modalViewOrder.toggle();
        } catch (error) {
            showAlertError(error);
        } finally {
            hideLoader();
        }
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, index: number) => {
        setMenuAnchor(event.currentTarget);
        setSelectedTransactionIndex(index);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setSelectedTransactionIndex(-1);
    };

    useEffect(() => {
        if (selectedCustomer) {
            void loadTransactions();
        }
    }, [selectedCustomer]);

    const handleClose = () => {
        modalPointsHistory.toggle();
    };

    const formatDate = (timestamp: Timestamp) => {
        return format(timestamp.toDate(), 'PPPpp', { locale: es });
    };

    return (
        <>
            <Dialog
                open={modalPointsHistory.modalOpen}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    style: { borderRadius: 16 }
                }}
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" fontWeight="bold">
                            Historial de Puntos - {selectedCustomer?.profile?.name || selectedCustomer?.phone}
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={3}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1">
                                Puntos actuales: <strong>{currentPoints.toFixed(2)}</strong>
                            </Typography>
                            {/* Lo recibido en toda su vida sin restar canjes — el mismo dato
                                que el CRM recibe como `contact.puntos_totales`. */}
                            <Typography variant="subtitle1" color="text.secondary">
                                Históricos: <strong>{getLifetimePointsFromTransactions(transactions)}</strong>
                            </Typography>
                        </Stack>

                        <TableContainer component={Paper} elevation={0}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Descripción</TableCell>
                                        <TableCell align="right">Puntos</TableCell>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell>Origen</TableCell>
                                        <TableCell>Otorgado por</TableCell>
                                        <TableCell>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {transactions.length > 0 ? (
                                        transactions.map((transaction, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{formatDate(transaction.date)}</TableCell>
                                                <TableCell>{transaction.description}</TableCell>
                                                <TableCell align="right">
                                                    <Chip
                                                        label={`${getTransactionSign(
                                                            transaction.type
                                                        )}${transaction.points.toFixed(2)}`}
                                                        color={getTransactionColor(transaction.type)}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>{getTransactionType(transaction.type)}</TableCell>
                                                <TableCell>{getTransactionSource(transaction.source)}</TableCell>
                                                <TableCell>
                                                    {transaction.createdBy
                                                        ? transaction.createdBy.profile.name
                                                        : 'System'}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={2}>
                                                        {transaction.source === PointTransactionSource.Order && (
                                                            <IconButton
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (
                                                                        transaction.source ===
                                                                            PointTransactionSource.Order &&
                                                                        transaction.referenceId
                                                                    ) {
                                                                        void handleOpenOrderModal(transaction.referenceId);
                                                                    }
                                                                }}
                                                                size="small"
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        )}
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleOpenMenu(e, index)}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                No hay transacciones registradas
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button variant="contained" onClick={handleClose} fullWidth sx={{ margin: 1 }}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Menú contextual para transacciones */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu} onClick={handleCloseMenu}>
                <MenuItem onClick={() => handleDeleteTransaction(selectedTransactionIndex)}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Eliminar transacción</ListItemText>
                </MenuItem>
            </Menu>
            {currentOrder && (
                <ModalViewOrder modalViewOrder={modalViewOrder} order={currentOrder} onDeleted={loadTransactions} />
            )}
        </>
    );
};
